import { type ComponentType, type MouseEvent } from 'react';
import {
    CarIcon,
    ClockIcon,
    FileVideoIcon,
    type IconProps,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { parseEngineSourceType, SourceType } from '@/types/source';
import { formatDate, formatRelativeTime } from '@/utils/date';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import type { Vehicle } from '@/types/vehicle';
import { Checkbox } from '../Checkbox';
import { Thumbnail } from '../Thumbnail';
import {
    DirectoryCard,
    type DirectoryCardMetadataItem,
} from '../DirectoryCard';

const SOURCE_TYPE_ICONS: Record<SourceType, ComponentType<IconProps>> = {
    [SourceType.CAMERA]: SecurityCameraIconMirrored,
    [SourceType.FLIR]: SecurityCameraIconMirrored,
    [SourceType.FILE]: FileVideoIcon,
};

interface VehicleCardProps {
    vehicle: Vehicle;
    thumbnailUrl?: string;
    checked?: boolean;
    isDeleting?: boolean;
    selectionDisabled?: boolean;
    onClick?: (id: string, event?: MouseEvent<HTMLDivElement>) => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onMarkAsRecognized?: (id: string) => void;
    onMarkAsUnknown?: (id: string) => void;
    onMerge?: () => void;
}

export function VehicleCard({
    vehicle,
    thumbnailUrl,
    checked,
    isDeleting,
    selectionDisabled,
    onClick,
    onEdit,
    onDelete,
    onMarkAsRecognized,
    onMarkAsUnknown,
    onMerge,
}: VehicleCardProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);

    const displayName = vehicle.name
        ? vehicle.name
        : `${t('vehicles.card.unknownVehicle')} ${capitalize(vehicle.vehicleType)}`;

    const tagLabels = vehicle.tags ?? [];

    const lastEvent = vehicle.lastEvent;
    const hasLastEvent = lastEvent != null;
    const seenAt = hasLastEvent
        ? (vehicle.lastSeen ?? vehicle.createdAt)
        : vehicle.createdAt;
    const sourceName = hasLastEvent
        ? (lastEvent.sourceName ?? vehicle.lastSourceName ?? '')
        : '';
    const showSource = hasLastEvent && sourceName.length > 0;
    const sourceKind = showSource
        ? parseEngineSourceType(lastEvent.sourceType ?? vehicle.lastSourceType)
        : undefined;
    const SourceIcon = sourceKind
        ? SOURCE_TYPE_ICONS[sourceKind]
        : SecurityCameraIconMirrored;

    const metadata: DirectoryCardMetadataItem[] = [
        {
            icon: ClockIcon,
            text: t(
                hasLastEvent
                    ? 'vehicles.card.seenAgo'
                    : 'vehicles.card.addedAgo',
                { time: formatRelativeTime(seenAt) },
            ),
            title: formatDate(seenAt),
            textClassName: 'lowercase first-letter:uppercase',
        },
        ...(showSource
            ? [{ icon: SourceIcon, text: sourceName, title: sourceName }]
            : []),
    ];

    const image = (
        <>
            <Thumbnail
                size="fill"
                imageSrc={thumbnailUrl}
                licensePlate={vehicle.licensePlate}
                placeholderIcon={
                    <CarIcon weight="fill" className="size-8 text-grey-400" />
                }
            />
            {checked !== undefined && (
                <div className="absolute top-1 left-1 z-10 pointer-events-none">
                    <Checkbox checked={checked} disabled={selectionDisabled} />
                </div>
            )}
        </>
    );

    return (
        <DirectoryCard
            testId="vehicle-card"
            title={displayName}
            image={image}
            metadata={metadata}
            tagLabels={tagLabels}
            isKnown={vehicle.known}
            isDeleting={isDeleting}
            disabled={selectionDisabled}
            actions={{
                editLabel: t('vehicles.card.edit'),
                deleteLabel:
                    !vehicle.known && onDelete
                        ? t('vehicles.card.delete')
                        : undefined,
                markAsUnknownLabel: vehicle.known
                    ? t('vehicles.card.markAsUnknown')
                    : undefined,
                mergeLabel: t('mergeVehicleModal.mergeInto'),
                onEdit: onEdit ? () => onEdit(vehicle.id) : undefined,
                onDelete:
                    !vehicle.known && onDelete
                        ? () => onDelete(vehicle.id)
                        : undefined,
                onMarkAsUnknown:
                    vehicle.known && onMarkAsUnknown
                        ? () => onMarkAsUnknown(vehicle.id)
                        : undefined,
                onMerge,
            }}
            markAsRecognizedLabel={t('vehicles.card.markAsRecognized')}
            onClick={
                onClick ? (event) => onClick(vehicle.id, event) : undefined
            }
            onMarkAsRecognized={
                onMarkAsRecognized
                    ? () => onMarkAsRecognized(vehicle.id)
                    : undefined
            }
        />
    );
}
